import { useMemo, useState } from 'react'
import type { ChangeEvent } from 'react'
import { Button } from '../../../shared/ui'
import { toast } from '../../../lib/toast'
import {
  autoMatchColumns,
  buildHistoryRows,
  IMPORT_FIELDS,
  importOutreachHistory,
  parseHistoryCsv,
  type ColumnMap,
  type ImportField,
  type ImportTotals,
  type ParsedHistory,
} from '../../../lib/outreachConnectors'

/**
 * "Import outreach history": a CSV of past sends from any tool, into the
 * active business. Pick a file, check the column matches, preview, import.
 * Re-importing the same file adds nothing new.
 */

const PREVIEW_ROWS = 5

function readText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(new Error('Could not read that file.'))
    reader.readAsText(file)
  })
}

export function summarySentence(t: ImportTotals, skipped: number): string {
  const parts = [
    `${t.activities_added.toLocaleString()} ${t.activities_added === 1 ? 'activity' : 'activities'} added`,
    `${t.contacts_created.toLocaleString()} new ${t.contacts_created === 1 ? 'contact' : 'contacts'}`,
  ]
  if (t.duplicates) parts.push(`${t.duplicates.toLocaleString()} already in Hunts`)
  const bad = t.invalid + skipped
  if (bad) parts.push(`${bad.toLocaleString()} without a valid email`)
  return `Done. ${parts.join(', ')}.`
}

type Step = 'pick' | 'map' | 'importing' | 'done'

export function HistoryImport({
  businessName,
  onImported,
}: {
  businessName: string
  onImported?: () => void
}) {
  const [step, setStep] = useState<Step>('pick')
  const [fileName, setFileName] = useState('')
  const [parsed, setParsed] = useState<ParsedHistory | null>(null)
  const [map, setMap] = useState<ColumnMap>({})
  const [error, setError] = useState('')
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [summary, setSummary] = useState('')

  const built = useMemo(() => (parsed ? buildHistoryRows(parsed, map) : null), [parsed, map])

  const reset = () => {
    setStep('pick')
    setParsed(null)
    setMap({})
    setFileName('')
    setError('')
  }

  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    setError('')
    setSummary('')
    if (!file) return
    try {
      const p = parseHistoryCsv(await readText(file))
      if (p.headers.length === 0 || p.rows.length === 0) {
        setError('That file has no rows. Export it again with a header row.')
        return
      }
      setFileName(file.name)
      setParsed(p)
      setMap(autoMatchColumns(p.headers))
      setStep('map')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read that file.')
    }
  }

  const setField = (f: ImportField, value: string) => {
    setMap((m) => {
      const next = { ...m }
      if (value === '') delete next[f]
      else next[f] = Number(value)
      return next
    })
  }

  const start = async () => {
    if (!built || built.rows.length === 0) return
    setStep('importing')
    setError('')
    setProgress({ done: 0, total: built.rows.length })
    try {
      const totals = await importOutreachHistory(built.rows, fileName, (done, total) =>
        setProgress({ done, total }),
      )
      const s = summarySentence(totals, built.skipped)
      setSummary(s)
      toast(s, 'success')
      setStep('done')
      onImported?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed.')
      setStep('map')
    }
  }

  const mappedFields = IMPORT_FIELDS.filter((f) => map[f.key] !== undefined)

  return (
    <div className="ot-card">
      {step === 'pick' && (
        <div className="ot-block">
          <label className="ot-dim" htmlFor="ot-history-file">
            A CSV export of past sends from any tool. One line per email sent. Goes into{' '}
            {businessName}.
          </label>
          <input
            id="ot-history-file"
            type="file"
            accept=".csv,text/csv"
            aria-label="Outreach history CSV"
            onChange={(e) => void pick(e)}
          />
          {summary && <div className="ot-ok">{summary}</div>}
        </div>
      )}

      {(step === 'map' || step === 'importing') && parsed && built && (
        <div className="ot-block" role="region" aria-label="Match columns">
          <div className="ot-row">
            <span className="ot-name">{fileName}</span>
            <span className="ot-dim">{parsed.rows.length.toLocaleString()} lines</span>
          </div>
          <p className="ot-sub">Check the matches. Only Email is required.</p>
          <div className="ot-map">
            {IMPORT_FIELDS.map((f) => (
              <FieldSelect
                key={f.key}
                field={f.key}
                label={f.required ? `${f.label} (required)` : f.label}
                headers={parsed.headers}
                value={map[f.key]}
                disabled={step === 'importing'}
                onChange={(v) => setField(f.key, v)}
              />
            ))}
          </div>

          {map.email === undefined ? (
            <div className="ot-error" role="alert">
              Pick the column that has the email address.
            </div>
          ) : (
            <>
              <div className="ot-table-wrap">
                <table className="ot-table" aria-label="Preview">
                  <thead>
                    <tr>
                      {mappedFields.map((f) => (
                        <th key={f.key}>{f.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {built.rows.slice(0, PREVIEW_ROWS).map((r, i) => (
                      <tr key={i}>
                        {mappedFields.map((f) => (
                          <td key={f.key}>{r[f.key] ?? ''}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="ot-dim">
                {built.rows.length.toLocaleString()} ready
                {built.skipped ? ` · ${built.skipped.toLocaleString()} without a valid email` : ''}
              </div>
            </>
          )}

          {step === 'importing' && (
            <div className="ot-block" aria-live="polite">
              <div
                className="ot-progress"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={progress.total}
                aria-valuenow={progress.done}
              >
                <div
                  style={{
                    width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%`,
                  }}
                />
              </div>
              <div className="ot-dim">
                Imported {progress.done.toLocaleString()} of {progress.total.toLocaleString()}
              </div>
            </div>
          )}

          {error && (
            <div className="ot-error" role="alert">
              {error}
            </div>
          )}

          <div className="ot-actions">
            <Button
              onClick={() => void start()}
              disabled={step === 'importing' || map.email === undefined || built.rows.length === 0}
            >
              {step === 'importing'
                ? 'Importing...'
                : `Import ${built.rows.length.toLocaleString()} ${built.rows.length === 1 ? 'line' : 'lines'}`}
            </Button>
            <Button variant="secondary" onClick={reset} disabled={step === 'importing'}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {step === 'done' && (
        <div className="ot-block">
          <div className="ot-ok" role="status">
            {summary}
          </div>
          <div className="ot-actions">
            <Button variant="secondary" onClick={reset}>
              Import another file
            </Button>
          </div>
        </div>
      )}

      {step === 'pick' && error && (
        <div className="ot-error" role="alert">
          {error}
        </div>
      )}
    </div>
  )
}

function FieldSelect({
  field,
  label,
  headers,
  value,
  disabled,
  onChange,
}: {
  field: ImportField
  label: string
  headers: string[]
  value: number | undefined
  disabled: boolean
  onChange: (v: string) => void
}) {
  const id = `ot-map-${field}`
  return (
    <>
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        className="settings-input"
        value={value === undefined ? '' : String(value)}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Skip</option>
        {headers.map((h, i) => (
          <option key={i} value={String(i)}>
            {h || `Column ${i + 1}`}
          </option>
        ))}
      </select>
    </>
  )
}
